// Wizard d'AJOUT / RECONFIGURATION d'équipement (SA-6.4) — poser une caisse, un Mobile Partner ou un poste
// Windows dans un magasin, en une seule traversée : type et régime de parc → appareil réel du parc →
// configuration embarquée → mise en service.
//
// ⚠️ Il s'ouvre TOUJOURS dans le contexte d'un magasin : « ajouter un équipement » sans savoir où le
// poser n'a pas de sens, et laisser choisir le magasin en dernier permettrait de configurer un terminal
// pour un magasin, puis de l'affecter à un autre.
//
// ⚠️ §23.0 — RIEN N'EST CRÉÉ AVANT L'ÉTAPE 4. Fermer le wizard aux étapes 1 à 3 ne laisse aucun objet
// derrière : l'affectation, la numérotation, la configuration et le jeton partent ENSEMBLE, au clic de
// mise en service. Un wizard qui écrit au fil des étapes laisse des appareils à moitié affectés que
// personne ne va chercher.
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, EmptyState , type IconName } from '@orgazone/ui-web';
import { dateTimeLong } from '@orgazone/date-format';
import { api, type DeviceRow, type TenantStore, type DevicePairingState, type DevicePairingActive, type DeviceConfigState } from '../api';
import { Pill } from '../ui';
import { qrSvg, AppInstallee } from './Fleet';
import { glassBoxSt, glassOverlaySt } from '../glass-modal';
import { DeviceName, deviceRef, deviceTitle } from '../device-name';
import type { EquipmentNature } from '../equipment-types';

export type EquipKind = 'caisse' | 'mobile-partner' | 'windows';
type Regime = 'courte' | 'longue' | 'achat';

/**
 * La nature de type (référentiel « Types d'équipement ») que chaque carte de l'assistant propose.
 * ⚠️ On range par la NATURE, jamais par le libellé : « Caisse tablette » et « Tablette Sliderz » sont deux types
 *    de nature caisse, et un appareil sans type n'est proposé nulle part — il ne déclencherait rien à l'affectation.
 */
const NATURE_OF: Record<EquipKind, EquipmentNature> = {
  caisse: 'caisse',
  'mobile-partner': 'mobile-partner',
  windows: 'poste',
};

const TYPES: { k: EquipKind; icon: IconName; label: string; badge: string; desc: string }[] = [
  { k: 'caisse', icon: 'cash', label: 'Caisse', badge: 'licence · numérotée R…', desc: "Point d'encaissement, entre dans la chaîne fiscale." },
  { k: 'mobile-partner', icon: 'barcode', label: 'Mobile Partner', badge: "terminal d'inventaire", desc: 'Inventaire, réception, transferts — hors chaîne fiscale.' },
  { k: 'windows', icon: 'grid', label: 'Client Windows', badge: 'poste BO', desc: "Poste d'administration, aucune configuration embarquée." },
];

const REGIMES: { k: Regime; icon: IconName; label: string; desc: string; meta: string }[] = [
  // ⚠️ Les métas ne décrivent que des mécanismes RÉELS. La « fenêtre de réservation ± 2 jours ouvrés »
  // de la maquette n'existe pas : rien ne réserve un appareil sur un créneau à venir (il n'y a pas de
  // planning), un appareil affecté sort simplement du parc disponible. L'annoncer ferait croire à une
  // protection contre le double engagement qui n'est pas en place (§23.0).
  { k: 'courte', icon: 'calendar', label: 'Location courte', desc: 'Renfort de quelques jours : inventaire, soldes, opération ponctuelle.', meta: 'matériel Orgazone · retour attendu' },
  { k: 'longue', icon: 'refresh', label: 'Location longue', desc: 'Sorti du pool et posé chez le client, avec une échéance.', meta: 'blocage automatique à échéance + 2 j' },
  { k: 'achat', icon: 'bag', label: 'Achat client', desc: 'Matériel vendu. Nous le pilotons toujours à distance, sans échéance.', meta: 'aucun retour attendu' },
];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const dansUnAn = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return iso(d); };

/** Libellés d'étapes — le fil d'ariane et la logique de saut lisent la MÊME liste (sinon ils divergent). */
const stepsFor = (kind: EquipKind): { n: number; label: string }[] => [
  { n: 1, label: 'Type & régime' },
  { n: 2, label: "Appareil" },
  // Un poste Windows n'embarque aucune configuration : lui montrer une étape vide serait une promesse creuse.
  ...(kind === 'windows' ? [] : [{ n: 3, label: 'Configuration' }]),
  { n: 4, label: 'Mise en service' },
];

// ── Coquille de la modale : la coque verre partagée (`glass-modal`) ─────────────────────────
// ⚠️ Le clic sur le voile NE ferme PAS : un wizard est un travail en cours, et l'écart de la souris
// coûterait ici tout le paramétrage déjà saisi. Seule la croix ferme.

const noteSt: CSSProperties = { fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.55 };

/** Champ VERROUILLÉ : un fait imposé, montré pour être lu et non pour être changé. */
function Locked({ k, v, note }: { k: string; v: ReactNode; note?: string }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 5 }}>{k}</div>
      <div className="field-input mono" style={{ background: 'var(--ink100)', cursor: 'default', fontWeight: 600 }}>{v}</div>
      {note && <div style={{ ...noteSt, marginTop: 5 }}>{note}</div>}
    </div>
  );
}

export function EquipmentWizard({ store: st, tenantName, stores, mode = 'add', initialKind = 'caisse', presetDevice, registerNumber, tokenValidUntil, onClose, onDone }: {
  store: TenantStore;
  tenantName: string;
  /** Tous les magasins du CLIENT — candidats de la liste Mobile Partner (le magasin courant en tête). */
  stores: TenantStore[];
  /** `config` = reconfigurer un terminal posé (étape 3) · `pair` = redonner un QR à une caisse existante (étape 4). */
  mode?: 'add' | 'config' | 'pair';
  initialKind?: EquipKind;
  /** Reconfiguration / réappairage : appareil DÉJÀ affecté. */
  presetDevice?: { id: string; name: string | null; family: string; model: string | null; serial: string | null };
  /** N° de caisse déjà attribué (mode `pair`) — il ne change PAS au réappairage. */
  registerNumber?: number | null;
  /** Échéance du jeton ENCORE VALIDE, si l'appelant en connaît un (mode `pair`). Décide seule de
   *  ré-afficher le QR en cours plutôt que d'en émettre un neuf — sans attendre aucune lecture. */
  tokenValidUntil?: string | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [kind, setKind] = useState<EquipKind>(initialKind);
  const [regime, setRegime] = useState<Regime>('courte');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [step, setStep] = useState(mode === 'config' ? 3 : mode === 'pair' ? 4 : 1);
  const [pool, setPool] = useState<DeviceRow[] | null>(null);
  const [poolErr, setPoolErr] = useState<string | null>(null);
  const [pick, setPick] = useState<DeviceRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Numéro RÉELLEMENT attribué par le serveur (étape 4) — jamais celui prédit à l'étape 3.
  const [attribue, setAttribue] = useState<number | null>(null);

  const devId = presetDevice?.id ?? pick?.id ?? null;

  // ── Configuration embarquée (Mobile Partner) ───────────────────────────────────────────────────
  const [cfg, setCfg] = useState<DeviceConfigState | null>(null);
  // Liste ORDONNÉE des magasins accessibles + ceux qui sont décochés (retirés sans perdre leur rang).
  const [ordre, setOrdre] = useState<string[]>([]);
  const [coches, setCoches] = useState<Set<string>>(new Set());
  const [defaut, setDefaut] = useState<string | null>(null);
  const [droits, setDroits] = useState<Set<string>>(new Set());

  // ── Mise en service ──────────────────────────────────────────────────────────────────────
  // ⚠️ DEUX états, et c'est délibéré : le JETON (créé en une écriture, instantané) et l'ÉTAT APPLICATIF
  // (qui interroge le BO et le store). Les fondre laissait le QR attendre des informations dont il n'a
  // pas besoin — l'opérateur regardait un écran vide alors que le code à scanner existait déjà.
  const [jeton, setJeton] = useState<DevicePairingActive | null>(null);
  const [pairing, setPairing] = useState<DevicePairingState | null>(null);
  const [jetonErr, setJetonErr] = useState<string | null>(null);   // échec de régénération → carte QR
  const [pairErr, setPairErr] = useState<string | null>(null);     // échec de lecture d'état → panneau droit
  const [regenBusy, setRegenBusy] = useState(false);

  const etapes = stepsFor(kind);

  // Parc DISPONIBLE de la famille voulue. Rechargé au changement de type : le compteur « N sur M »
  // doit parler du parc qu'on regarde, pas du précédent.
  useEffect(() => {
    if (mode !== 'add') return;
    let vivant = true;
    setPool(null); setPoolErr(null); setPick(null);
    api.devices()
      .then((r) => { if (vivant) setPool(r.devices); })
      .catch((e) => { if (vivant) setPoolErr((e as Error).message); });
    return () => { vivant = false; };
  }, [kind, mode]);

  const ofKind = useMemo(() => (pool ?? []).filter((d) => d.nature === NATURE_OF[kind]), [pool, kind]);
  const dispos = useMemo(() => ofKind.filter((d) => d.status === 'available'), [ofKind]);

  // Configuration : lue au serveur pour un appareil déjà affecté (reconfiguration), amorcée sur les
  // magasins du client sinon. ⚠️ Le magasin courant EN TÊTE — c'est celui qu'on équipe.
  useEffect(() => {
    if (kind !== 'mobile-partner') return;
    const base = [st.id, ...stores.filter((s) => s.id !== st.id).map((s) => s.id)];
    // ⚠️ LA GARDE COUVRE LES DEUX BRANCHES : la lecture « sans appareil » ne rend que le catalogue des droits, et revenue
    //    après celle d'un appareil choisi entre-temps, elle remplaçait sa configuration réelle par une configuration vide.
    let vivant = true;
    if (mode !== 'config' || !devId) {
      setOrdre(base); setCoches(new Set(base)); setDefaut(st.id);
      setDroits(new Set(['mobile-partner.inventaire', 'mobile-partner.reception', 'mobile-partner.transfert', 'mobile-partner.consultation', 'mobile-partner.creation_article']));
      api.deviceConfig(devId ?? '')
        .then((c) => { if (vivant) setCfg(c); })
        .catch(() => { /* ⚠️ Best-effort : sans appareil choisi, seul le CATALOGUE de droits manque — il est reproposé à l'étape 3 une fois l'appareil connu. */ });
      return () => { vivant = false; };
    }
    api.deviceConfig(devId).then((c) => {
      if (!vivant) return;
      setCfg(c);
      // Les magasins configurés d'abord, dans leur ordre ; les autres à la suite, décochés.
      const reste = base.filter((id) => !c.storeOrder.includes(id));
      setOrdre([...c.storeOrder, ...reste]);
      setCoches(new Set(c.storeOrder));
      setDefaut(c.defaultStoreId ?? c.storeOrder[0] ?? null);
      setDroits(new Set(c.rights));
    }).catch((e) => { if (vivant) setErr((e as Error).message); });
    return () => { vivant = false; };
  }, [kind, mode, devId, st.id, stores]);

  // Catalogue de droits : sans appareil sélectionné la première lecture échoue (route par appareil).
  // Relu dès qu'un appareil est retenu — sinon l'étape 3 n'aurait aucune puce à offrir.
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : choisir un autre terminal pendant la lecture offrirait à l'étape 3 les
  //    droits lus pour l'ANCIEN appareil — et, l'état posé, celui du terminal choisi ne serait plus jamais relu.
  useEffect(() => {
    if (kind !== 'mobile-partner' || cfg || !devId) return;
    let alive = true;
    api.deviceConfig(devId)
      .then((c) => { if (alive) setCfg(c); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [kind, cfg, devId]);

  const nomMagasin = useCallback((id: string) => stores.find((s) => s.id === id)?.name ?? '(magasin inconnu)', [stores]);

  /**
   * MODE RÉAPPAIRAGE — deux situations que l'on ne traite PAS pareil (spec §4) :
   * • il y a un jeton qui court encore → on RÉ-AFFICHE l'actuel. En générer un nouveau invaliderait
   *   un QR peut-être déjà imprimé ou envoyé, sans que personne l'ait demandé.
   * • sinon (caisse déjà mise en service, ou jeton expiré) → on émet AUSSITÔT un jeton neuf, ce qui
   *   révoque l'ancien. C'est le but du geste : le jeton d'une tablette remplacée ne doit plus valoir.
   *
   * ⚠️ LA DÉCISION SE PREND SUR CE QUE L'APPELANT SAIT DÉJÀ (`tokenValidUntil`, lu avec la fiche
   * magasin), et JAMAIS en interrogeant d'abord l'état applicatif. Ce dernier consulte le BO et le
   * store : le mettre sur le chemin du QR le retardait de plusieurs secondes, alors qu'il n'entre pas
   * dans la décision. Les deux appels partent donc EN PARALLÈLE — le QR n'attend que lui-même.
   */
  useEffect(() => {
    if (mode !== 'pair' || !devId) return;
    let vivant = true;
    // Réutiliser le jeton en cours : sa VALEUR n'est connue que du serveur, on la prend dans l'état
    // relu. C'est le seul cas où le QR dépend de cette lecture, et c'est assumé — on ne peut pas
    // afficher un secret qu'on n'a pas.
    const reutiliser = !!tokenValidUntil && new Date(tokenValidUntil).getTime() > Date.now();
    api.devicePairing(devId)
      .then((etat) => { if (!vivant) return; setPairing(etat); if (reutiliser && etat.active) setJeton(etat.active); })
      .catch((e) => { if (vivant) setPairErr((e as Error).message); });

    if (!reutiliser) {
      setRegenBusy(true);
      api.createPairing(devId)
        .then((a) => { if (vivant) { setJeton(a); setPairing((p) => (p ? { ...p, active: a } : p)); } })
        .catch((e) => { if (vivant) setJetonErr((e as Error).message); })
        .finally(() => { if (vivant) setRegenBusy(false); });
    }
    return () => { vivant = false; };
  }, [mode, devId, tokenValidUntil]);

  /** Changer de régime REMET LES DATES À ZÉRO — une échéance à un an ne doit jamais fuir dans une location courte. */
  const changerRegime = (r: Regime) => {
    setRegime(r);
    if (r === 'longue') { setDebut(iso(new Date())); setFin(dansUnAn()); }
    else { setDebut(''); setFin(''); }
  };

  const bougerMagasin = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= ordre.length) return;
    const copie = [...ordre];
    [copie[i], copie[j]] = [copie[j]!, copie[i]!];
    setOrdre(copie);
  };
  const basculerMagasin = (id: string) => {
    const n = new Set(coches);
    if (n.has(id)) {
      n.delete(id);
      // ⚠️ Retirer le magasin par défaut VIDE le défaut : le laisser pointer vers un magasin interdit
      // enverrait le terminal démarrer sur une zone qu'il n'a plus le droit d'ouvrir.
      if (defaut === id) setDefaut(null);
    } else n.add(id);
    setCoches(n);
  };
  const basculerDroit = (code: string) => {
    const n = new Set(droits);
    if (n.has(code)) n.delete(code); else n.add(code);
    setDroits(n);
  };

  const ordreCoche = ordre.filter((id) => coches.has(id));

  /** Enregistre la seule configuration (reconfiguration) — sans toucher au parc ni au régime. */
  const enregistrerConfig = async () => {
    if (!devId) return;
    setBusy(true); setErr(null);
    try {
      await api.saveDeviceConfig(devId, { defaultStoreId: defaut ?? ordreCoche[0]!, storeOrder: ordreCoche, rights: [...droits] });
      onDone(`Configuration enregistrée — ${presetDevice ? deviceRef(presetDevice) : 'le terminal'} la tirera à son prochain réveil.`);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  /**
   * MISE EN SERVICE — le seul point d'écriture du wizard : affectation (+ numérotation atomique côté
   * serveur), configuration embarquée, puis émission du jeton. Un échec en route arrête tout et montre
   * le message RÉEL : on ne passe jamais à l'étape 4 sur une création partielle.
   */
  const mettreEnService = async () => {
    if (!pick) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.deviceAction(pick.id, {
        action: 'assign', storeId: st.id, rentalType: regime,
        ...(regime !== 'achat' && debut ? { rentalStart: debut } : {}),
        ...(regime !== 'achat' && fin ? { rentalEnd: fin } : {}),
      });
      setAttribue((r as { registerNumber?: number | null }).registerNumber ?? null);
      if (kind === 'mobile-partner') {
        await api.saveDeviceConfig(pick.id, { defaultStoreId: defaut ?? ordreCoche[0]!, storeOrder: ordreCoche, rights: [...droits] });
      }
      // Le jeton fait passer à l'étape 4 À LUI SEUL : c'est lui qu'on est venu chercher.
      setJeton(await api.createPairing(pick.id));
      setStep(4);
      // L'état applicatif arrive ensuite, sans retenir le QR. ⚠️ Son échec ne remonte PAS dans `err` :
      // il n'invalide rien de ce qui vient d'être créé — le panneau de droite dit son propre problème.
      api.devicePairing(pick.id).then(setPairing).catch((e) => setPairErr((e as Error).message));
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const regenerer = async () => {
    if (!devId) return;
    setRegenBusy(true); setJetonErr(null);
    try {
      const actif = await api.createPairing(devId);
      setJeton(actif);
      setPairing((p) => (p ? { ...p, active: actif } : p));
    } catch (e) { setJetonErr((e as Error).message); }
    finally { setRegenBusy(false); }
  };

  // ── Avancement : libellé, validité, action ────────────────────────────────────────────────
  const suivant: { label: string; ok: boolean; go: () => void } = (() => {
    if (step === 1) return { label: "Choisir l'appareil →", ok: true, go: () => setStep(2) };
    if (step === 2) {
      if (!pick) return { label: 'Sélectionnez un appareil', ok: false, go: () => {} };
      // Windows : aucune configuration embarquée → l'étape 3 n'existe pas pour lui.
      return kind === 'windows'
        ? { label: busy ? 'Mise en service…' : 'Mettre en service et générer le QR', ok: !busy, go: () => void mettreEnService() }
        : { label: 'Configurer →', ok: true, go: () => setStep(3) };
    }
    if (step === 3) {
      if (kind === 'mobile-partner' && ordreCoche.length === 0) return { label: 'Au moins un magasin accessible', ok: false, go: () => {} };
      if (mode === 'config') return { label: busy ? 'Enregistrement…' : 'Enregistrer la configuration', ok: !busy, go: () => void enregistrerConfig() };
      return { label: busy ? 'Mise en service…' : 'Mettre en service et générer le QR', ok: !busy, go: () => void mettreEnService() };
    }
    return {
      label: 'Terminer', ok: true,
      go: () => onDone(mode === 'pair'
        ? `QR d'appairage prêt${registerNumber != null ? ` — caisse n° ${registerNumber}` : ''}. Rien d'autre n'a changé : ni le numéro, ni la licence, ni les compteurs.`
        : `${pick ? deviceRef(pick) : 'Appareil'} affecté à ${st.name}${attribue != null ? ` — caisse n° ${attribue}` : ''}.`),
    };
  })();

  /** Note de pied, contextuelle à l'étape — elle explique la CONSÉQUENCE du bouton, pas son libellé. */
  const notePied = step === 1
    ? "Le régime décide de ce qui arrive à l'appareil ensuite : retour au pool, échéance, ou rien."
    : step === 2
      ? 'Le numéro de série affiché vient de la fiche de parc — vérifiez la machine que vous avez en main.'
      : step === 3
        ? (mode === 'config' ? "L'appareil tirera cette configuration à son prochain réveil." : "Rien n'a encore été créé : tout part au clic.")
        : "Le jeton et la clé ne transitent que par ce QR, une seule fois.";

  // Le jeton fraîchement créé fait foi ; l'état relu ne sert que si l'on rouvre sur un jeton existant.
  const actif = jeton ?? pairing?.active ?? null;
  const heuresRestantes = actif ? Math.max(0, Math.round((new Date(actif.expiresAt).getTime() - Date.now()) / 3600000)) : 0;

  return (
    <div style={glassOverlaySt}>
      <div style={glassBoxSt(720)} role="dialog" aria-modal="true">
        {/* ── En-tête fixe : le titre, puis le chemin parcouru ── */}
        <div style={{ padding: '16px 20px 13px', borderBottom: '1px solid var(--ink200)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-.02em' }}>
                {mode === 'config' ? `Reconfigurer ${presetDevice ? deviceRef(presetDevice) : ''}`
                  : mode === 'pair' ? `Appairage de la caisse n° ${registerNumber ?? '—'}`
                    : `Ajouter un équipement — ${st.name}`}
              </h3>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3 }}>
                {tenantName} · <span className="mono">GLN {st.gln}</span>
              </div>
            </div>
            <button className="btn-icon" onClick={onClose} aria-label="Fermer" style={{ width: 30, height: 30, flexShrink: 0 }}>✕</button>
          </div>

          {/* Fil d'ariane — le retour est permis, jamais le saut en avant : une étape future n'a pas
              encore de saisie valide, y sauter afficherait un écran vide sans le dire. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 11, flexWrap: 'wrap' }}>
            {etapes.map((e, i) => (
              <span key={e.n} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {i > 0 && <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink300)' }}>›</span>}
                {e.n < step && mode === 'add' ? (
                  <button className="btn-link" onClick={() => setStep(e.n)} style={{ fontSize: 'var(--t-note)', color: 'var(--ink600)' }}>{e.label}</button>
                ) : (
                  <span style={{ fontSize: 'var(--t-note)', fontWeight: e.n === step ? 700 : 500, color: e.n === step ? 'var(--ink900)' : 'var(--ink400)' }}>{e.label}</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* ── Corps scrollable ── */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {err && <div className="err" style={{ marginBottom: 14 }}>{err}</div>}

          {/* ═══ ÉTAPE 1 — type & régime ═══ */}
          {step === 1 && (
            <>
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, marginBottom: 9 }}>Type d'équipement</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 9 }}>
                {TYPES.map((t) => (
                  <button aria-pressed={kind === t.k} key={t.k} onClick={() => setKind(t.k)} className={kind === t.k ? 'row-pick selected' : 'row-pick'}
                    style={{ padding: '13px 13px 12px', textAlign: 'left', color: 'inherit' }}>
                    <Icon name={t.icon} size={19} color={kind === t.k ? 'var(--ok-fg)' : 'var(--ink500)'} />
                    <div style={{ fontSize: 'var(--t-dense)', fontWeight: 700, marginTop: 8 }}>{t.label}</div>
                    <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 3 }}>{t.badge}</div>
                    <div style={{ ...noteSt, marginTop: 7 }}>{t.desc}</div>
                  </button>
                ))}
              </div>

              <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, margin: '18px 0 9px' }}>Régime de parc</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 9 }}>
                {REGIMES.map((r) => (
                  <button key={r.k} onClick={() => changerRegime(r.k)} className={regime === r.k ? 'row-pick selected' : 'row-pick'}
                    style={{ padding: '13px 13px 12px', textAlign: 'left', color: 'inherit' }}>
                    <Icon name={r.icon} size={19} color={regime === r.k ? 'var(--ok-fg)' : 'var(--ink500)'} />
                    <div style={{ fontSize: 'var(--t-dense)', fontWeight: 700, marginTop: 8 }}>{r.label}</div>
                    <div style={{ ...noteSt, marginTop: 5 }}>{r.desc}</div>
                    <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', marginTop: 7 }}>{r.meta}</div>
                  </button>
                ))}
              </div>

              {/* Un achat n'a ni début ni fin : afficher deux champs vides suggérerait une échéance qui n'existe pas. */}
              {regime !== 'achat' && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 5 }}>{regime === 'longue' ? 'Début du contrat' : 'Début'}</div>
                      <input type="date" aria-label={regime === 'longue' ? 'Début du contrat' : 'Début'} className="field-input" value={debut} onChange={(e) => setDebut(e.target.value)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 5 }}>{regime === 'longue' ? 'Échéance' : 'Retour prévu'}</div>
                      <input type="date" aria-label={regime === 'longue' ? 'Échéance' : 'Retour prévu'} className="field-input" value={fin} onChange={(e) => setFin(e.target.value)} />
                    </div>
                  </div>
                  <div style={{ ...noteSt, marginTop: 8 }}>
                    {/* ⚠️ Ces deux notes disent ce que le système FAIT, pas ce qu'on aimerait qu'il fasse.
                        Côté courte, le transit est une consigne d'exploitation (aucune réservation de
                        créneau n'existe) ; côté longue, le blocage automatique est réel — il compte
                        2 jours CALENDAIRES, comme la règle qui le pose. */}
                    {regime === 'courte'
                      ? "Comptez 2 jours ouvrés de transit de part et d'autre : l'appareil n'est ni parti ni revenu le jour dit. Aucun créneau n'est réservé pour autant — l'appareil quitte le parc disponible dès l'affectation, et n'y revient qu'au retour."
                      : 'Échéance proposée à un an par défaut — modifiable. Dépassée de 2 jours, un blocage automatique s’applique à l’appareil : seul un super-admin peut le lever.'}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ═══ ÉTAPE 2 — appareil au parc ═══ */}
          {step === 2 && (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Appareil disponible au parc</span>
                <div style={{ flex: 1 }} />
                {pool && <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{dispos.length} sur {ofKind.length} du parc</span>}
              </div>

              {poolErr && <div className="err">{poolErr}</div>}
              {!pool && !poolErr && <div className="muted">Lecture du parc…</div>}

              {pool && dispos.length === 0 && (
                <EmptyState icon="cash" title="Aucun appareil disponible"
                  note={`Aucun « ${TYPES.find((t) => t.k === kind)!.label} » n'est disponible : les appareils du parc sont soit déjà affectés, soit en transit, soit indisponibles sur la fenêtre demandée. Ajoutez-en un au registre de la flotte, ou libérez-en un par un retour au parc.`} />
              )}

              {pool && dispos.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {dispos.map((d) => (
                    <button key={d.id} onClick={() => setPick(d)} className={pick?.id === d.id ? 'row-pick selected' : 'row-pick'}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '11px 13px', textAlign: 'left', color: 'inherit' }}>
                      <span style={{
                        width: 15, height: 15, borderRadius: 'var(--r-pill)', flexShrink: 0, marginTop: 2,
                        border: `2px solid ${pick?.id === d.id ? 'var(--ok-fg)' : 'var(--ink300)'}`,
                        background: pick?.id === d.id ? 'var(--ok-fg)' : 'transparent',
                        boxShadow: pick?.id === d.id ? 'inset 0 0 0 2.5px var(--paper)' : 'none',
                      }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {/* Le nom qu'on lui a donné au parc, puis son modèle (ou son type) : ce qu'on lit pour choisir. */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}><DeviceName d={d} /></span>
                          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink700)' }}>{d.model ?? d.family}</span>
                        </div>
                        {/* ⚠️ Le n° de série est AFFICHÉ, jamais saisi : il est capté une seule fois à
                            l'entrée au parc. Le montrer ici sert à vérifier qu'on affecte la bonne
                            machine physique — une saisie ici ruinerait le contrôle anti-substitution. */}
                        <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 3 }}>
                          {d.serial ? `SN ${d.serial}` : 'aucun n° de série au parc'} · {d.id.slice(0, 8)}
                          {d.lastSeenAt ? ` · dernier contact ${dateTimeLong(d.lastSeenAt)}` : ' · jamais mis en service'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ═══ ÉTAPE 3a — CAISSE : rien à choisir ═══ */}
          {step === 3 && kind === 'caisse' && (
            <>
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, marginBottom: 4 }}>Configuration de la caisse</div>
              <div style={{ ...noteSt, marginBottom: 14 }}>
                Une caisse ne choisit rien : sa numérotation est attribuée à l'affectation et son sens de synchronisation
                est celui du magasin. Ces quatre faits sont montrés pour être vérifiés, pas réglés.
              </div>
              <div style={{ display: 'grid', gap: 13 }}>
                <Locked k="Numéro de caisse attribué"
                  v={<>R{String((st.registerCounter ?? 0) + 1).padStart(2, '0')} <Pill tone="wa">prévu</Pill></>}
                  note="Attribué définitivement par le serveur au moment de la mise en service. Un numéro retiré n'est jamais réutilisé." />
                <Locked k="Racine des compteurs" v={st.sCode ? `S${st.sCode} · GLN ${st.gln}` : `GLN ${st.gln}`}
                  note={st.sCode ? undefined : 'Le numéro de magasin se fige à la première vente — la racine sera complète à ce moment.'} />
                <Locked k="Sens de synchronisation" v="BO → caisse (catalogue) · caisse → BO (ventes)"
                  note="Hérité du magasin, non réglable par appareil." />
                <Locked k="Mode hors-ligne" v="encaissement autorisé · rejeu à la reconnexion" />
              </div>
            </>
          )}

          {/* ═══ ÉTAPE 3b — MOBILE PARTNER ═══ */}
          {step === 3 && kind === 'mobile-partner' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13, marginBottom: 16 }}>
                <Locked k="Magasin d'origine" v={st.name} note="Rattachement de facturation. En changer impose un retour au parc puis une réaffectation." />
                <Locked k="Magasin par défaut" v={defaut ? nomMagasin(defaut) : '— aucun'} note="Zone de départ des rétrocessions, inventaires et BL. Se règle dans la liste ci-dessous." />
              </div>

              <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, marginBottom: 4 }}>Magasins accessibles</div>
              <div style={{ ...noteSt, marginBottom: 9 }}>
                L'ordre est propre à cet appareil : les magasins fréquents en tête. Il descend au terminal et n'est pas modifiable sur place.
              </div>
              <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                {ordre.map((id, i) => {
                  const on = coches.has(id);
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', borderTop: i ? '1px solid var(--ink100)' : undefined, opacity: on ? 1 : 0.5 }}>
                      <button onClick={() => basculerMagasin(id)} className="btn-icon sm" aria-label={on ? 'Retirer ce magasin' : 'Rendre ce magasin accessible'}
                        style={{ color: on ? 'var(--ok-fg)' : 'var(--ink400)' }}>{on ? '✓' : '○'}</button>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 'var(--t-dense)', fontWeight: 600 }}>{nomMagasin(id)}</div>
                        <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{stores.find((s) => s.id === id)?.gln ?? ''}</div>
                      </div>
                      {/* ⚠️ Définir un défaut sur une ligne décochée est INTERDIT : le terminal démarrerait
                          sur un magasin qu'il n'a pas le droit d'ouvrir. */}
                      <button onClick={() => on && setDefaut(id)} disabled={!on}
                        className={defaut === id ? 'field-opt selected' : 'field-opt'}
                        title={on ? undefined : "Rendez d'abord ce magasin accessible."}
                        style={{ height: 26, fontSize: 'var(--t-meta)', ...(on ? {} : { cursor: 'not-allowed' }) }}>
                        {defaut === id ? 'défaut' : 'définir'}
                      </button>
                      <button onClick={() => bougerMagasin(i, -1)} disabled={i === 0} className="pg-btn" aria-label={`Monter ${nomMagasin(id)}`}>↑</button>
                      <button onClick={() => bougerMagasin(i, 1)} disabled={i === ordre.length - 1} className="pg-btn" aria-label={`Descendre ${nomMagasin(id)}`}>↓</button>
                    </div>
                  );
                })}
              </div>

              <div style={{ fontSize: 'var(--t-body)', fontWeight: 700, margin: '18px 0 9px' }}>Droits du terminal</div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {(cfg?.availableRights ?? []).map((r) => (
                  <button key={r.code} onClick={() => basculerDroit(r.code)} title={r.code}
                    className={droits.has(r.code) ? 'chip active' : 'chip'}>{r.label}</button>
                ))}
                {!cfg && <span className="muted" style={{ fontSize: 'var(--t-dense)' }}>Lecture des capacités du terminal…</span>}
              </div>
              <div style={{ ...noteSt, marginTop: 9 }}>
                Les opérateurs autorisés et leurs PIN restent gérés par l'admin du client dans son back-office :
                ici on ouvre les capacités de l'appareil, pas les personnes.
              </div>

              <div style={{ display: 'flex', gap: 9, marginTop: 16, padding: '11px 13px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)' }}>
                <span style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: 'var(--in-fg)', marginTop: 6, flexShrink: 0 }} />
                <div style={noteSt}>L'appareil TIRE cette configuration au prochain réveil : rien ne lui est poussé.</div>
              </div>
            </>
          )}

          {/* ═══ ÉTAPE 4 — mise en service ═══ */}
          {step === 4 && (
            <>
              {/* ⚠️ Un appareil bloqué S'APPAIRE quand même : le blocage porte sur l'USAGE, pas sur
                  l'appairage. Le taire ferait repartir du magasin en croyant la caisse remise en marche,
                  alors qu'elle refusera d'ouvrir — le déplacement serait perdu. */}
              {pairing?.blocked && (
                <div style={{ display: 'flex', gap: 10, padding: '11px 13px', marginBottom: 14, borderRadius: 'var(--r-sm)', border: '1px solid var(--wa-fg)', background: 'var(--wa-bg)' }}>
                  <span style={{ flexShrink: 0 }}>⚠️</span>
                  <div style={{ fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
                    <b>Cette caisse est actuellement bloquée</b> ({pairing.blocked.scope === 'subscription' ? "abonnement du client" : pairing.blocked.scope === 'store' ? 'magasin' : 'caisse'}) :
                    elle s'appairera mais <b>restera bloquée à l'ouverture</b>. Motif : {pairing.blocked.reason}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,260px) minmax(0,1fr)', gap: 14, alignItems: 'start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, padding: 16, borderRadius: 'var(--r-sm)', border: '1px solid var(--wa-fg)', background: 'var(--wa-bg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>QR d'appairage</span>
                    <div style={{ flex: 1 }} />
                    {actif && <Pill tone="wa">usage unique · {heuresRestantes} h</Pill>}
                  </div>
                  {jetonErr && <div className="err" style={{ width: '100%' }}>{jetonErr}</div>}
                  {!actif && !jetonErr && <div className="muted">Émission du jeton…</div>}
                  {actif && (
                    <>
                      {/* ⚠️ Blanc EN DUR : un QR se décode par contraste, sa marge doit rester blanche
                          même en thème sombre — sinon l'appareil ne lit plus le code. */}
                      <div style={{ background: '#fff', padding: 10, borderRadius: 'var(--r-xs)', width: 138, lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: qrSvg(actif.url) }} />
                      <div className="mono" style={{ fontSize: 'var(--t-sub)', overflowWrap: 'anywhere', textAlign: 'center' }}>{actif.token.slice(0, 18)}…</div>
                      <div style={{ ...noteSt, textAlign: 'center' }}>
                        Consommé au premier scan. Expire le {dateTimeLong(actif.expiresAt)}. Un QR permanent équivaudrait
                        à une clé d'accès affichée en clair — il n'en existe pas.
                      </div>
                      {/* ⚠️ On énonce EXACTEMENT ce qui a été révoqué. La clé de sync est celle du
                          MAGASIN et vaut pour toutes ses caisses : la couper ici arrêterait le magasin
                          entier. Écrire « la session de la caisse est révoquée » ferait croire qu'une
                          tablette perdue est neutralisée — elle ne l'est que par un BLOCAGE, qu'on nomme. */}
                      {mode === 'pair' && pairing?.redeemedAt && (
                        <div style={{ ...noteSt, textAlign: 'center', borderTop: '1px solid var(--wa-fg)', paddingTop: 8 }}>
                          L'ancien jeton est révoqué, et la clé de configuration de cet appareil est invalidée.
                          En revanche la caisse continue de synchroniser avec la clé du <b>magasin</b> tant qu'elle n'est
                          pas bloquée : pour arrêter une tablette perdue, posez un blocage.
                        </div>
                      )}
                      <button className="btn-ghost sm" disabled={regenBusy} onClick={() => void regenerer()}>{regenBusy ? '…' : 'Révoquer et régénérer'}</button>
                    </>
                  )}
                </div>
                {/* Le panneau charge à son rythme, derrière le QR : il interroge le BO et le store. */}
                {pairing ? <AppInstallee state={pairing} />
                  : pairErr ? <div className="err">État applicatif indisponible : {pairErr}</div>
                    : <div className="muted">Lecture de l'état applicatif…</div>}
              </div>

              {/* Ce que le jeton porte — le récapitulatif de ce qui vient d'être écrit, pas une promesse. */}
              <div style={{ marginTop: 14, border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--ink200)', background: 'var(--head, var(--ink-50))', fontSize: 'var(--t-body)', fontWeight: 700 }}>
                  Ce que le jeton porte
                </div>
                {(() => {
                  // ⚠️ En réappairage le numéro est CELUI QUI EXISTE DÉJÀ : rien n'a été attribué, et
                  // afficher « aucune » laisserait croire que la caisse a perdu sa place dans la chaîne.
                  const numero = mode === 'pair' ? registerNumber ?? null : attribue;
                  return [
                    ['Appareil', `${(pick ?? presetDevice) ? deviceTitle((pick ?? presetDevice)!) : '—'}${(pick?.model ?? presetDevice?.model) ? ` · ${pick?.model ?? presetDevice?.model}` : ''}${(pick?.serial ?? presetDevice?.serial) ? ` · SN ${pick?.serial ?? presetDevice?.serial}` : ''}`],
                    ['Magasin', `${st.name} · GLN ${st.gln}`],
                    ['Numérotation', numero != null ? `caisse n° ${numero}${st.sCode ? ` · racine S${st.sCode}` : ''} — inchangée` : 'aucune — hors chaîne fiscale'],
                    ...(mode === 'pair' ? [] : [['Régime de parc', REGIMES.find((r) => r.k === regime)!.label + (regime !== 'achat' && fin ? ` · jusqu'au ${fin}` : '')] as [string, string]]),
                    ['Configuration', kind === 'mobile-partner'
                      ? `${ordreCoche.length} magasin${ordreCoche.length > 1 ? 's' : ''} accessible${ordreCoche.length > 1 ? 's' : ''} · défaut ${defaut ? nomMagasin(defaut) : '—'} · ${droits.size} droit${droits.size > 1 ? 's' : ''}`
                      : 'héritée du magasin (pull)'],
                  ] as [string, string][];
                })().map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink100)', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', width: 128, flexShrink: 0 }}>{k}</span>
                    <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{v}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Pied fixe ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 20px', borderTop: '1px solid var(--ink200)', flexShrink: 0 }}>
          {/* À l'étape 1 il n'y a pas d'arrière : le retour ferme, ce que le libellé dit. */}
          {/* ⚠️ En réappairage il n'y a rien derrière : type, appareil et configuration existent déjà.
              Le bouton gauche FERME (il ne recule pas), et fermer est sans risque — le jeton est émis. */}
          <button className="btn-ghost" onClick={() => (step === 1 || mode !== 'add' ? onClose() : setStep(step - 1))} disabled={mode === 'add' && step === 4}>
            {step === 1 || mode !== 'add' ? 'Fermer' : '← Retour'}
          </button>
          <div style={{ flex: 1 }} />
          <div style={{ ...noteSt, maxWidth: 330, textAlign: 'right' }}>{notePied}</div>
          <button className="btn-cta" onClick={suivant.go} disabled={!suivant.ok}
            style={suivant.ok ? undefined : { background: 'var(--ink200)', color: 'var(--ink400)', cursor: 'not-allowed' }}>
            {suivant.label}
          </button>
        </div>
      </div>
    </div>
  );
}
